import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Truck, Plus, Search, Pencil, Trash2, X, PackageCheck, Send, Ban, Eye, ArrowLeft, ClipboardList } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const ta = "w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A] resize-none";
const PINK = "#C4738A";

const PO_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-500" },
  dipesan: { label: "Dipesan", cls: "bg-amber-50 text-amber-600" },
  diterima: { label: "Diterima", cls: "bg-emerald-50 text-emerald-600" },
  batal: { label: "Batal", cls: "bg-red-50 text-red-500" },
};
const blankSup = { name: "", phone: "", email: "", address: "", note: "" };

export default function AdminSuppliers() {
  const [tab, setTab] = useState("supplier");
  return (
    <div data-testid="suppliers-page" className="space-y-5 pb-10">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Supplier & Pembelian</h1>
        <p className="text-xs text-slate-400">Data supplier bersifat internal — tidak pernah tampil di web store maupun struk POS.</p>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[{ id: "supplier", label: "Data Supplier" }, { id: "po", label: "Pembelian (PO)" }].map((t) => (
          <button key={t.id} data-testid={`sup-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "supplier" ? <SupplierTab /> : <PurchaseTab />}
    </div>
  );
}

function SupplierTab() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankSup);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/suppliers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => (items || []).filter((s) => [s.name, s.phone, s.email].join(" ").toLowerCase().includes(q.toLowerCase())), [items, q]);
  const { paged: pagedSup, pager: supPager } = usePager(filtered);

  const startAdd = () => { setEditing(null); setForm(blankSup); setShowForm(true); };
  const startEdit = (s) => { setEditing(s.id); setForm({ name: s.name, phone: s.phone || "", email: s.email || "", address: s.address || "", note: s.note || "" }); setShowForm(true); };
  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Nama supplier wajib diisi (min. 2 karakter)");
    setBusy(true);
    try {
      if (editing) { await api.put(`/admin/suppliers/${editing}`, form); toast.success("Supplier diperbarui"); }
      else { await api.post("/admin/suppliers", form); toast.success("Supplier ditambahkan"); }
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan supplier"); }
    finally { setBusy(false); }
  };
  const del = async (s) => {
    if (!window.confirm(`Hapus supplier "${s.name}"? Tag supplier akan dilepas dari ${s.product_count} produk.`)) return;
    try {
      const { data } = await api.delete(`/admin/suppliers/${s.id}`);
      toast.success(`Supplier dihapus${data.detached ? ` — tag dilepas dari ${data.detached} produk` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, WA, email..." className={`${inp} w-full pl-8`} />
        </div>
        <span className="text-[11px] text-slate-400">{items?.length ?? 0} supplier</span>
        <button data-testid="supplier-add-btn" onClick={startAdd} className="ml-auto h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Tambah Supplier
        </button>
      </div>

      {showForm && (
        <div data-testid="supplier-form" className="bg-white rounded-2xl border border-[#C4738A]/30 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">{editing ? "Ubah Supplier" : "Supplier Baru"}</h3>
            <button onClick={() => setShowForm(false)} className="w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Nama Supplier *</label>
              <input data-testid="supplier-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="PT Sumber Kain Cirebon" className={`${inp} w-full`} /></div>
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">No. WhatsApp / Telepon</label>
              <input data-testid="supplier-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="08xxxxxxxxxx" className={`${inp} w-full`} /></div>
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Email</label>
              <input data-testid="supplier-email" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="sales@supplier.com" className={`${inp} w-full`} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Alamat</label>
              <textarea data-testid="supplier-address" value={form.address} onChange={(e) => setF("address", e.target.value)} rows={2} placeholder="Alamat lengkap supplier" className={ta} /></div>
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Catatan Internal</label>
              <textarea data-testid="supplier-note" value={form.note} onChange={(e) => setF("note", e.target.value)} rows={2} placeholder="Termin pembayaran, minimal order, dsb." className={ta} /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
            <button data-testid="supplier-save" onClick={save} disabled={busy} className="h-9 px-5 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold disabled:opacity-50">{editing ? "Simpan Perubahan" : "Tambah Supplier"}</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Supplier</th>
              <th className="text-left px-3 py-2.5 font-semibold">Kontak</th>
              <th className="text-left px-3 py-2.5 font-semibold">Alamat</th>
              <th className="text-center px-3 py-2.5 font-semibold w-20">Produk</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={5} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {items?.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-xs text-slate-400">Belum ada supplier. Klik "Tambah Supplier" untuk membuat.</td></tr>}
            {pagedSup.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 text-[#C4738A] flex items-center justify-center flex-shrink-0"><Truck className="w-4 h-4" /></div>
                    <div><div className="text-xs font-semibold text-slate-800">{s.name}</div>
                      {s.note && <div className="text-[10px] text-slate-400 line-clamp-1">{s.note}</div>}</div>
                  </div>
                </td>
                <td className="px-3 py-3 text-[11px] text-slate-600"><div>{s.phone || "—"}</div><div className="text-slate-400">{s.email || ""}</div></td>
                <td className="px-3 py-3 text-[11px] text-slate-500 max-w-[240px]"><span className="line-clamp-2">{s.address || "—"}</span></td>
                <td className="px-3 py-3 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.product_count > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{s.product_count}</span></td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`supplier-edit-${s.id}`} onClick={() => startEdit(s)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`supplier-delete-${s.id}`} onClick={() => del(s)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items && items.length > 0 && !filtered.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">Tidak ada supplier yang cocok.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager {...supPager} testid="supplier-pager" />
    </div>
  );
}

function PurchaseTab() {
  const [items, setItems] = useState(null);
  const [statusF, setStatusF] = useState("all");
  const [mode, setMode] = useState("list");
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    setItems(null);
    api.get("/admin/purchases", { params: { status: statusF } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [statusF]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (po, st) => {
    try {
      await api.patch(`/admin/purchases/${po.id}/status`, { status: st });
      toast.success(`PO ${po.code} → ${PO_STATUS[st].label}`);
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };
  const delPo = async (po) => {
    if (!window.confirm(`Hapus PO ${po.code}?`)) return;
    try { await api.delete(`/admin/purchases/${po.id}`); toast.success("PO dihapus"); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  if (mode === "create") return <PurchaseCreate onDone={(msg) => { setMode("list"); load(); if (msg) toast.success(msg); }} onCancel={() => setMode("list")} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[{ id: "all", label: "Semua" }, ...Object.entries(PO_STATUS).map(([id, v]) => ({ id, label: v.label }))].map((f) => (
          <button key={f.id} data-testid={`po-filter-${f.id}`} onClick={() => setStatusF(f.id)}
            className={`h-8 px-4 rounded-full text-[11px] font-semibold border transition-colors ${statusF === f.id ? "border-[#C4738A] text-[#C4738A] bg-rose-50/50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{f.label}</button>
        ))}
        <button data-testid="po-create-btn" onClick={() => setMode("create")} className="ml-auto h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Buat PO
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Kode PO</th>
              <th className="text-left px-3 py-2.5 font-semibold">Supplier</th>
              <th className="text-center px-3 py-2.5 font-semibold">Item</th>
              <th className="text-right px-3 py-2.5 font-semibold">Total</th>
              <th className="text-center px-3 py-2.5 font-semibold">Status</th>
              <th className="text-left px-3 py-2.5 font-semibold">Dibuat</th>
              <th className="px-3 py-2.5 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {items?.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400"><ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-200" />Belum ada purchase order. Klik "Buat PO" untuk memesan stok ke supplier.</td></tr>}
            {items?.map((po) => (
              <tr key={po.id} data-testid={`po-row-${po.id}`}>
                <td className="px-4 py-3 text-xs font-mono font-semibold text-slate-800">{po.code}</td>
                <td className="px-3 py-3 text-xs text-slate-600">{po.supplier_name}</td>
                <td className="px-3 py-3 text-center text-xs text-slate-500">{po.items.reduce((a, i) => a + i.qty, 0)} pcs</td>
                <td className="px-3 py-3 text-right text-xs font-bold text-slate-900">{formatIDR(po.total)}</td>
                <td className="px-3 py-3 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PO_STATUS[po.status]?.cls}`}>{PO_STATUS[po.status]?.label}</span></td>
                <td className="px-3 py-3 text-[11px] text-slate-400">{formatDateID(po.created_at)}</td>
                <td className="px-3 py-3 text-right">
                  <button data-testid={`po-detail-${po.id}`} onClick={() => setDetail(po)} className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center ml-auto transition-colors"><Eye className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="po-detail-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDetail(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2"><h3 className="text-sm font-bold text-slate-900 font-mono">{detail.code}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PO_STATUS[detail.status]?.cls}`}>{PO_STATUS[detail.status]?.label}</span></div>
                <p className="text-[11px] text-slate-400 mt-0.5">{detail.supplier_name} • dibuat {formatDateID(detail.created_at)} oleh {detail.created_by}</p>
              </div>
              <button onClick={() => setDetail(null)} className="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <table className="w-full">
                <thead><tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="text-left py-1.5 font-semibold">Produk</th><th className="text-center py-1.5 font-semibold w-16">Qty</th>
                  <th className="text-right py-1.5 font-semibold w-24">Harga Beli</th><th className="text-right py-1.5 font-semibold w-24">Subtotal</th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {detail.items.map((it, i) => (
                    <tr key={i}><td className="py-2"><div className="text-xs font-semibold text-slate-800">{it.name}</div>
                      <div className="text-[10px] text-slate-400">{[it.variant, it.sku].filter(Boolean).join(" • ")}</div></td>
                      <td className="py-2 text-center text-xs">{it.qty}</td>
                      <td className="py-2 text-right text-xs">{formatIDR(it.cost)}</td>
                      <td className="py-2 text-right text-xs font-semibold">{formatIDR(it.subtotal)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t border-slate-100"><td colSpan={3} className="py-2.5 text-right text-[11px] font-semibold text-slate-500">Total Pembelian</td>
                  <td className="py-2.5 text-right text-sm font-bold text-[#C4738A]">{formatIDR(detail.total)}</td></tr></tfoot>
              </table>
              {detail.note && <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-3">Catatan: {detail.note}</div>}
              {detail.status === "dipesan" && <p className="text-[10px] text-amber-600 bg-amber-50 rounded-lg p-2.5">Penerimaan barang saat ini belum menambah stok otomatis — penyesuaian stok masih manual lewat edit produk. Integrasi stok segera hadir.</p>}
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex gap-2 justify-end flex-wrap">
              {(detail.status === "draft" || detail.status === "batal") && (
                <button data-testid="po-delete-btn" onClick={() => delPo(detail)} className="h-9 px-4 rounded-lg border border-red-200 text-red-500 text-xs font-semibold flex items-center gap-1.5 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /> Hapus</button>
              )}
              {detail.status === "draft" && (<>
                <button data-testid="po-cancel-btn" onClick={() => setStatus(detail, "batal")} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Batalkan</button>
                <button data-testid="po-send-btn" onClick={() => setStatus(detail, "dipesan")} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Kirim Pesanan ke Supplier</button>
              </>)}
              {detail.status === "dipesan" && (<>
                <button data-testid="po-cancel-btn2" onClick={() => setStatus(detail, "batal")} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Batalkan</button>
                <button data-testid="po-receive-btn" onClick={() => setStatus(detail, "diterima")} className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5"><PackageCheck className="w-3.5 h-3.5" /> Terima Barang</button>
              </>)}
              {detail.received_at && <span className="text-[10px] text-slate-400 self-center">Diterima {formatDateID(detail.received_at)}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PurchaseCreate({ onDone, onCancel }) {
  const [suppliers, setSuppliers] = useState([]);
  const [opts, setOpts] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => {
      const flat = [];
      r.data.forEach((p) => {
        const vs = Array.isArray(p.variants) && p.variants.length ? p.variants : [null];
        vs.forEach((v) => flat.push({
          key: `${p.id}|${v ? v.sku : ""}`, product_id: p.id, name: p.name,
          variant: v ? [v.color, v.size].filter(Boolean).join(" / ") : "",
          sku: v ? v.sku : p.sku, cost: (v && v.cost_price) || p.cost_price || 0,
        }));
      });
      setOpts(flat);
    }).catch(() => {});
  }, []);

  const addRow = () => setRows((rs) => [...rs, { key: "", qty: 1, cost: 0 }]);
  const pick = (i, key) => {
    const o = opts.find((x) => x.key === key);
    setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, key, product_id: o?.product_id, name: o?.name, variant: o?.variant, sku: o?.sku, cost: o?.cost || 0 } : r));
  };
  const setR = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const total = rows.filter((r) => r.product_id).reduce((a, r) => a + (parseInt(r.qty) || 0) * (parseInt(r.cost) || 0), 0);

  const save = async (status) => {
    if (!supplierId) return toast.error("Pilih supplier dulu");
    const items = rows.filter((r) => r.product_id);
    if (!items.length) return toast.error("Tambahkan minimal 1 item pembelian");
    setBusy(status);
    try {
      const { data } = await api.post("/admin/purchases", {
        supplier_id: supplierId, note, status,
        items: items.map((r) => ({ product_id: r.product_id, name: r.name, variant: r.variant, sku: r.sku, qty: parseInt(r.qty) || 1, cost: parseInt(r.cost) || 0 })),
      });
      onDone(`PO ${data.code} tersimpan (${status === "draft" ? "draft" : "dipesan"})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan PO"); setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="po-create">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 flex items-center justify-center hover:border-[#C4738A] hover:text-[#C4738A] transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        <div><h2 className="font-display text-lg font-bold text-slate-900">Buat Purchase Order</h2>
          <p className="text-[11px] text-slate-400">Pesanan pembelian stok ke supplier</p></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Supplier *</label>
            <select data-testid="po-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={`${inp} w-full`}>
              <option value="">— Pilih supplier —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.phone ? ` (${s.phone})` : ""}</option>)}
            </select>
            {!suppliers.length && <p className="text-[10px] text-amber-500 mt-1">Belum ada supplier — tambahkan dulu di tab Data Supplier.</p>}
          </div>
          <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Catatan</label>
            <input data-testid="po-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Termin, perkiraan kirim, dsb." className={`${inp} w-full`} /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-semibold text-slate-500">Item Pembelian *</label>
            <button data-testid="po-add-item" onClick={addRow} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold flex items-center gap-1 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors"><Plus className="w-3 h-3" /> Tambah Item</button>
          </div>
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead><tr className="text-[10px] uppercase text-slate-400 bg-slate-50/60 border-b border-slate-100">
                <th className="text-left px-3 py-2 font-semibold">Produk / Variasi</th>
                <th className="text-center px-2 py-2 font-semibold w-20">Qty</th>
                <th className="text-right px-2 py-2 font-semibold w-32">Harga Beli</th>
                <th className="text-right px-3 py-2 font-semibold w-28">Subtotal</th>
                <th className="w-10"></th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {!rows.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-slate-400">Belum ada item — klik "Tambah Item".</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`po-item-${i}`}>
                    <td className="px-3 py-2">
                      <select data-testid={`po-item-product-${i}`} value={r.key} onChange={(e) => pick(i, e.target.value)} className={`${inp} w-full max-w-md`}>
                        <option value="">— Pilih produk / variasi —</option>
                        {opts.map((o) => <option key={o.key} value={o.key}>{o.name}{o.variant ? ` — ${o.variant}` : ""} ({o.sku})</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2"><input data-testid={`po-item-qty-${i}`} type="number" min="1" value={r.qty} onChange={(e) => setR(i, "qty", e.target.value)} className={`${inp} w-full text-center`} /></td>
                    <td className="px-2 py-2"><input data-testid={`po-item-cost-${i}`} type="number" min="0" value={r.cost} onChange={(e) => setR(i, "cost", e.target.value)} className={`${inp} w-full text-right`} /></td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-slate-800">{formatIDR((parseInt(r.qty) || 0) * (parseInt(r.cost) || 0))}</td>
                    <td className="py-2 text-center"><button onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} className="w-7 h-7 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center mx-auto"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-slate-100 bg-slate-50/40"><td colSpan={3} className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500">Total Pembelian</td>
                <td data-testid="po-total" className="px-3 py-2.5 text-right text-sm font-bold text-[#C4738A]">{formatIDR(total)}</td><td></td></tr></tfoot>
            </table>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
          <button data-testid="po-save-draft" onClick={() => save("draft")} disabled={!!busy} className="h-9 px-4 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold hover:border-[#C4738A] transition-colors disabled:opacity-50">Simpan sebagai Draft</button>
          <button data-testid="po-save-order" onClick={() => save("dipesan")} disabled={!!busy} className="h-9 px-5 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Simpan & Pesan</button>
        </div>
      </div>
    </div>
  );
}
